import { Injectable } from '@angular/core';
import { EmployeeRole, Permission } from '../models/AuthUser';

@Injectable({
  providedIn: 'root'
})
export class RoleService {

  // Map Azure AD Security Groups to Application Roles
  private readonly GROUP_TO_ROLE_MAP: Record<string, EmployeeRole[]> = {
    'NLC-Administrators-Dev': [EmployeeRole.ADMINISTRATOR],
    'NLC-HeadOf-Dev': [EmployeeRole.HEAD_OF],
    'NLC-Sales-Dev': [EmployeeRole.SALES],
    'NLC-ReadOnly-Dev': [EmployeeRole.READ_ONLY]
  };

  // Map Azure AD App Role claim values to Application Roles
  private readonly APPROLE_TO_ROLE_MAP: Record<string, EmployeeRole[]> = {
    'Administrator': [EmployeeRole.ADMINISTRATOR],
    'HeadOf': [EmployeeRole.HEAD_OF],
    'Sales': [EmployeeRole.SALES],
    'ReadOnly': [EmployeeRole.READ_ONLY]
  };

  // Map Roles to Permissions
  private readonly ROLE_PERMISSIONS: Record<EmployeeRole, Permission[]> = {
    [EmployeeRole.ADMINISTRATOR]: [
      Permission.MANAGE_USERS,
      Permission.MANAGE_SYSTEM,
      Permission.VIEW_AUDIT_LOGS,
      Permission.MANAGE_ORDERS,
      Permission.MANAGE_CUSTOMERS,
      Permission.VIEW_REPORTS,
      Permission.VIEW_CUSTOMER_DATA,
      Permission.MANAGE_SUPPORT_TICKETS,
      Permission.VIEW_DASHBOARD,
      Permission.VIEW_OWN_PROFILE
    ],
    // HeadOf replaces former Manager permissions
    [EmployeeRole.HEAD_OF]: [
      Permission.MANAGE_ORDERS,
      Permission.MANAGE_CUSTOMERS,
      Permission.VIEW_REPORTS,
      Permission.VIEW_CUSTOMER_DATA,
      Permission.MANAGE_SUPPORT_TICKETS,
      Permission.VIEW_DASHBOARD,
      Permission.VIEW_OWN_PROFILE
    ],
    // Sales: restricted set, no admin or head-of capabilities
    [EmployeeRole.SALES]: [
      Permission.VIEW_CUSTOMER_DATA,
      Permission.MANAGE_SUPPORT_TICKETS,
      Permission.VIEW_DASHBOARD,
      Permission.VIEW_OWN_PROFILE
    ],
    [EmployeeRole.READ_ONLY]: [
      Permission.VIEW_DASHBOARD,
      Permission.VIEW_OWN_PROFILE
    ]
  };

  /**
   * Convert Azure AD group names to application roles
   */
  mapGroupsToRoles(groups: string[]): EmployeeRole[] {
    const roles: EmployeeRole[] = [];

    groups.forEach(group => {
      //console.log(`Mapping group: ${group}`);
      const mappedRoles = this.GROUP_TO_ROLE_MAP[group];
      if (mappedRoles) {
        roles.push(...mappedRoles);
      }
    });

    // Remove duplicates and return
    return [...new Set(roles)];
  }

  /**
   * Convert app-role values (from 'roles' claim) to application roles
   */
  mapAppRolesToRoles(appRoles: string[]): EmployeeRole[] {
    const roles: EmployeeRole[] = [];
    appRoles.forEach(value => {
      const mapped = this.APPROLE_TO_ROLE_MAP[value];
      if (mapped) roles.push(...mapped);
    });
    return [...new Set(roles)];
  }

  /**
   * Get all permissions for given roles
   */
  getPermissionsForRoles(roles: EmployeeRole[]): Permission[] {
    const permissions: Permission[] = [];

    roles.forEach(role => {
      const rolePermissions = this.ROLE_PERMISSIONS[role];
      if (rolePermissions) {
        permissions.push(...rolePermissions);
      }
    });

    // Remove duplicates and return
    return [...new Set(permissions)];
  }

  /**
   * Check if user has specific permission
   */
  hasPermission(userPermissions: Permission[], requiredPermission: Permission): boolean {
    return userPermissions.includes(requiredPermission);
  }

  /**
   * Check if user has any of the specified permissions
   */
  hasAnyPermission(userPermissions: Permission[], requiredPermissions: Permission[]): boolean {
    return requiredPermissions.some(permission => userPermissions.includes(permission));
  }

  /**
   * Check if user has all of the specified permissions
   */
  hasAllPermissions(userPermissions: Permission[], requiredPermissions: Permission[]): boolean {
    return requiredPermissions.every(permission => userPermissions.includes(permission));
  }

  /**
   * Get the highest role (for display purposes)
   */
  getHighestRole(roles: EmployeeRole[]): EmployeeRole | null {
    const roleHierarchy = [
      EmployeeRole.ADMINISTRATOR,
      EmployeeRole.HEAD_OF,
      EmployeeRole.SALES,
      EmployeeRole.READ_ONLY
    ];

    for (const role of roleHierarchy) {
      if (roles.includes(role)) {
        return role;
      }
    }

    return null;
  }

  /**
   * Get display name for role
   */
  getRoleDisplayName(role: EmployeeRole): string {
    const displayNames: Record<EmployeeRole, string> = {
      [EmployeeRole.ADMINISTRATOR]: 'Administrator',
      [EmployeeRole.HEAD_OF]: 'Head Of',
      [EmployeeRole.SALES]: 'Sales',
      [EmployeeRole.READ_ONLY]: 'Read Only'
    };

    return displayNames[role] || role;
  }
}
